import { MemoryMetric } from "@/api/studio/insights/types/memory.type";
import { AttentionMetric, AttentionSubMetrics } from "@/api/studio/insights/types/metrics.type";
import { EmotionMetric, ReportType } from "../contexts/reports-context";
import {
  getOriginalImageUrl,
  getHeatmapUrl,
  getAttentionVisibilityUrl,
  getAttentionClarityUrl,
  getAttentionDigestibilityUrl,
  getMemoryUrl,
  getEmotionUrl,
} from "@/api/renderer/api";
import { Visualisation } from "@/api/renderer/types/renderer.type";

/* Types */
export interface LayerState {
  showHotspots: boolean;
  showGazePath: boolean;
  showAOIRank: boolean;
}

/* Utility Functions */

/**
 * Gets the appropriate image URL based on report type and metrics
 */
export const getImageUrl = (
  reportType: ReportType,
  attentionMetric: AttentionMetric | undefined,
  visualisation: Visualisation,
  layers: LayerState,
  projectId: string,
  studyId: string,
  assetKey: string,
  contextKey: string | undefined,
  attentionSubMetric: AttentionSubMetrics | undefined,
  memoryMetric: MemoryMetric | undefined,
  emotionMetric: EmotionMetric | undefined,
): string => {
  const baseParams = { projectId, studyId, assetKey };

  if (visualisation === "none" && reportType === "creative") {
    return getOriginalImageUrl(baseParams);
  }

  // Creative Report
  if (reportType === "creative") {
    return getHeatmapUrl(
      {
        ...baseParams,
        heatmapType: visualisation === "heat" ? "heat" : "opacity",
      },
      visualisation,
    );
  }

  // Attention Reports
  if (reportType === "attention" && attentionMetric) {
    if (attentionMetric === "visibility" && contextKey) {
      return getAttentionVisibilityUrl({ ...baseParams, contextKey }, visualisation);
    }

    if (attentionMetric === "clarity" && attentionSubMetric) {
      return getAttentionClarityUrl(
        {
          ...baseParams,
          subMetric: attentionSubMetric,
          showHotspots: layers.showHotspots,
          showGazePath: layers.showGazePath,
        },
        visualisation,
      );
    }

    if (attentionMetric === "digestibility" && attentionSubMetric) {
      return getAttentionDigestibilityUrl(
        {
          ...baseParams,
          subMetric: attentionSubMetric,
          showAOIRank: layers.showAOIRank,
        },
        visualisation,
      );
    }
  }

  // Memory Report
  if (reportType === "memory" && memoryMetric) {
    return getMemoryUrl({ ...baseParams, metric: memoryMetric });
  }

  // Emotion Report
  if (reportType === "emotion" && emotionMetric) {
    return getEmotionUrl({ ...baseParams, metric: emotionMetric });
  }

  // Fallback to original
  return getOriginalImageUrl(baseParams);
};

/**
 * Determines if a report type should show the visualisation dropdown
 */
export const shouldShowVisualisationDropdown = (reportType: ReportType, attentionMetric?: AttentionMetric): boolean => {
  if (reportType === "creative") return true;
  if (reportType === "attention" && (attentionMetric === "visibility" || attentionMetric === "clarity" || attentionMetric === "digestibility")) return true;
  return false;
};

/**
 * Determines if a report type should show layer controls
 */
export const getLayerControls = (reportType: ReportType, attentionMetric?: AttentionMetric): "clarity" | "digestibility" | null => {
  if (reportType !== "attention") return null;
  if (attentionMetric === "clarity") return "clarity";
  if (attentionMetric === "digestibility") return "digestibility";
  return null;
};
